import React, { useState } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import LoadingState from '../shared/LoadingState';
import { Plus, Trash2, X, AlertTriangle, Layers, Calendar, Users, UserCheck } from 'lucide-react';

export default function MeetingTypesManagement() {
  const { meetingTypes, addMeetingType, deleteMeetingType, loading: dataLoading } = useAdmin();
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState<{ name: string; level: 'State' | 'District' | 'Unit' }>({
    name: '',
    level: 'State'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Warning modal state for blocked deletions
  const [blockedTypeInfo, setBlockedTypeInfo] = useState<{
    name: string;
    message: string;
  } | null>(null);

  const handleAddMeetingType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setLoading(true);
    setError('');

    const result = await addMeetingType({
      name: formData.name.trim(),
      level: formData.level
    });

    if (result.data) {
      setShowAddModal(false);
      setFormData({ name: '', level: 'State' });
    } else {
      setError(result.error?.message || 'Failed to add meeting type');
    }

    setLoading(false);
  };

  const handleDeleteMeetingType = async (type: any) => {
    if (!window.confirm(`Are you sure you want to delete "${type.name}"?`)) return;

    setLoading(true);
    setError('');

    const result = await deleteMeetingType(type.id);

    if (result.error) {
      if (result.details?.inUse) {
        setBlockedTypeInfo({
          name: type.name,
          message: result.error.message
        });
      } else {
        setError(result.error.message || 'Failed to delete meeting type');
      }
    }

    setLoading(false);
  };

  const getLevelBadgeColor = (level: string) => {
    switch (level) {
      case 'State':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'District':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Unit':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Meeting Types ({dataLoading ? '...' : meetingTypes.length})</h2>
            <p className="text-xs sm:text-sm text-gray-500">Configure organizational meeting types, levels, and view linked data metrics.</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus size={16} />
            <span>Add Meeting Type</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Meeting Types Content */}
        {dataLoading ? (
          <LoadingState title="Loading Meeting Types..." minHeight="min-h-[300px]" />
        ) : meetingTypes.length > 0 ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs sm:text-sm divide-y divide-gray-200 min-w-max">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Meeting Type Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Level</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Total Meetings</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Total Members</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Assigned Conveners</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {meetingTypes.map((type: any) => (
                    <tr key={type.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Layers size={16} className="text-blue-600 shrink-0" />
                          <span>{type.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getLevelBadgeColor(type.level)}`}>
                          {type.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center space-x-1 text-gray-700">
                          <Calendar size={14} className="text-gray-400" />
                          <span className="font-semibold text-gray-900">{type.meetingsCount || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center space-x-1 text-gray-700">
                          <Users size={14} className="text-gray-400" />
                          <span className="font-semibold text-gray-900">{type.membersCount || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center space-x-1 text-gray-700">
                          <UserCheck size={14} className="text-gray-400" />
                          <span className="font-semibold text-gray-900">{type.convenersCount || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleDeleteMeetingType(type)}
                          disabled={loading}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center ml-auto"
                          title="Delete Meeting Type"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3">
              {meetingTypes.map((type: any) => (
                <div key={type.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:bg-gray-50 transition space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                        <Layers size={18} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">{type.name}</h3>
                        <span className={`inline-block mt-0.5 px-2 py-0.5 text-[11px] font-semibold rounded-full border ${getLevelBadgeColor(type.level)}`}>
                          {type.level}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteMeetingType(type)}
                      disabled={loading}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition disabled:opacity-50 min-w-[36px] min-h-[36px] flex items-center justify-center"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100 text-center text-xs">
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="font-bold text-gray-900">{type.meetingsCount || 0}</div>
                      <div className="text-[10px] text-gray-500">Meetings</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="font-bold text-gray-900">{type.membersCount || 0}</div>
                      <div className="text-[10px] text-gray-500">Members</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="font-bold text-gray-900">{type.convenersCount || 0}</div>
                      <div className="text-[10px] text-gray-500">Conveners</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            No meeting types found. Add your first meeting type to get started!
          </div>
        )}
      </div>

      {/* Add Meeting Type Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6 space-y-4 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
            {/* Mobile Pull Indicator */}
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-1 sm:hidden shrink-0" />

            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900">Add New Meeting Type</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 min-w-[40px] min-h-[40px] flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs sm:text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleAddMeetingType} className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                  Meeting Type Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Executive Committee Meeting"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                  Level *
                </label>
                <select
                  value={formData.level}
                  onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value as any }))}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                >
                  <option value="State">State</option>
                  <option value="District">District</option>
                  <option value="Unit">Unit</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setError('');
                  }}
                  className="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50 min-h-[44px] shadow-sm flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <span>Add Meeting Type</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blocked Deletion Warning Modal */}
      {blockedTypeInfo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6 space-y-4 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
            {/* Mobile Pull Indicator */}
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-1 sm:hidden shrink-0" />

            <div className="flex items-center space-x-3 text-amber-600 border-b pb-3">
              <AlertTriangle size={24} className="shrink-0" />
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Cannot Delete Meeting Type</h3>
            </div>

            <div className="space-y-3">
              <p className="text-xs sm:text-sm text-gray-700">
                Deletion blocked for <span className="font-semibold text-gray-900">"{blockedTypeInfo.name}"</span>.
              </p>
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-xl text-xs space-y-1">
                <p className="font-semibold">{blockedTypeInfo.message}</p>
              </div>
              <p className="text-xs text-gray-500">
                To safely delete this meeting type, please reassign or delete the associated meetings, members, or convener assignments first.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setBlockedTypeInfo(null)}
                className="w-full sm:w-auto px-5 py-2.5 bg-gray-900 text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-gray-800 transition min-h-[44px]"
              >
                Understand & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
